import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mic, 
  MicOff, 
  Send, 
  Volume2, 
  VolumeX, 
  Briefcase,
  Layers,
  FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const OPENROUTER_KEY = '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';
const OPENROUTER_URL = 'https://openrouter.ai/api/v1/chat/completions';
const AI_MODELS = [
  'meta-llama/llama-4-maverick:free',
  'deepseek/deepseek-chat-v3-0324:free',
  'google/gemini-2.0-flash-exp:free',
  'meta-llama/llama-3.3-70b-instruct:free'
];

interface Topic {
  id: string;
  name: string;
  badge: string;
  desc: string;
  systemContext: string;
}

const TOPICS: Topic[] = [
  {
    id: 'Full Stack',
    name: 'Full Stack Developer',
    badge: 'Квалификация',
    desc: 'Фронтенд, бэкенд, REST/GraphQL, реляционные базы данных и микросервисы.',
    systemContext: 'Full Stack разработка: React/Next.js, Node.js/Python, SQL/NoSQL, Docker, архитектура систем.'
  },
  {
    id: 'Frontend',
    name: 'Frontend Engineer',
    badge: 'Интерфейсы',
    desc: 'React, TypeScript, рендеринг браузера, стейт-менеджеры, перформанс и CSS.',
    systemContext: 'Frontend разработка: React, Virtual DOM, браузерный рендеринг, Event Loop, оптимизация бандла, CSS.'
  },
  {
    id: 'Backend & FinTech',
    name: 'Backend & FinTech',
    badge: 'Банковский сектор',
    desc: 'Транзакции, ACID, очереди Kafka/RabbitMQ, безопасность платежей и API.',
    systemContext: 'Backend и FinTech: транзакции, идемпотентность платежей, ACID, безопасность банковских API, микросервисы, PostgreSQL/Redis.'
  },
  {
    id: 'AI & Data Science',
    name: 'AI & Data Science',
    badge: 'Аналитика и ИИ',
    desc: 'LLM, Prompt Engineering, NLP, эмбеддинги, трансформеры и прикладной ML.',
    systemContext: 'AI & Data: принципы LLM, эмбеддинги, трансформеры (mBERT, GPT), промпт-инжиниринг, валидация данных, метрики качества моделей.'
  },
  {
    id: 'DevOps & Cloud',
    name: 'DevOps & SRE',
    badge: 'Инфраструктура',
    desc: 'Docker, CI/CD, Kubernetes, Linux, Nginx, мониторинг и сетевые протоколы.',
    systemContext: 'DevOps: Docker контейнеризация, пайплайны CI/CD, Kubernetes, Linux sysadmin, безопасность инфраструктуры.'
  },
  {
    id: 'Digital HR & Recruiter',
    name: 'Оценка персонала / HR',
    badge: 'Аттестация',
    desc: 'Оценка цифровых компетенций кандидатов, грейдинг, скоринг и проведение интервью.',
    systemContext: 'HR и нанимающий менеджер: методология интервью по компетенциям, проверка хард- и софт-скиллов, валидность оценки.'
  }
];

interface ChatMessage {
  id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  evalBadge?: 'CORRECT' | 'PARTIAL' | 'WRONG';
  cleanContent?: string;
  timestamp: number;
}

export default function InterviewSimulator() {
  const navigate = useNavigate();

  const [selectedTopic, setSelectedTopic] = useState<Topic>(TOPICS[0]);
  const [selectedMode, setSelectedMode] = useState<'text' | 'voice'>('text');
  const [isStarted, setIsStarted] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceStatus, setVoiceStatus] = useState('Ожидание');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const recognitionRef = useRef<any>(null);
  const synthRef = useRef<SpeechSynthesis | null>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    }
    return () => {
      stopAllAudio();
    };
  }, []);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isLoading, transcript]);

  const stopAllAudio = () => {
    if (synthRef.current) {
      try { synthRef.current.cancel(); } catch { /* ignore */ }
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    setIsSpeaking(false);
    setIsListening(false);
  };

  const getSystemPrompt = (topic: Topic) => {
    return `Ты — технический экзаменатор и эксперт по отбору персонала в Study Platform и банковском секторе.
Специализация оценки: «${topic.name}».
Область знаний: ${topic.systemContext}.

Требования к проведению собеседования:
1. Задавай ровно один вопрос за раз.
2. Не повторяй заданные вопросы. Уровень сложности должен соответствовать профилю должности.
3. При ответе кандидата сразу укажи статус оценки:
   - [CORRECT] — ответ правильный и исчерпывающий.
   - [PARTIAL] — ответ частично верный, есть неточности.
   - [WRONG] — ответ неверный или не по существу.
4. После статуса в 1-3 предложениях дай объективный профессиональный комментарий.
5. Затем сразу сформулируй следующий вопрос.
6. Язык общения: русский. Стиль: деловой, строгий, академический. Без использования эмодзи и разговорных оборотов.`;
  };

  const startInterview = async () => {
    setIsStarted(true);
    setMessages([]);
    stopAllAudio();

    const sysMsg: ChatMessage = {
      id: 'sys',
      role: 'system',
      content: getSystemPrompt(selectedTopic),
      timestamp: Date.now()
    };

    const initialUserMsg: ChatMessage = {
      id: 'kickoff',
      role: 'user',
      content: `Здравствуйте. Я готов пройти оценку квалификации по направлению «${selectedTopic.name}». Задайте первый вопрос.`,
      timestamp: Date.now()
    };

    const updatedContext = [sysMsg, initialUserMsg];
    setIsLoading(true);
    setVoiceStatus('Генерация вопроса...');

    try {
      const reply = await callOpenRouterAPI(updatedContext);
      const parsed = parseAIResponse(reply);
      const aiMsg: ChatMessage = {
        id: String(Date.now()),
        role: 'assistant',
        content: reply,
        evalBadge: parsed.badge,
        cleanContent: parsed.cleanText,
        timestamp: Date.now()
      };
      setMessages([aiMsg]);

      if (selectedMode === 'voice') {
        setTranscript(parsed.cleanText);
        speakText(parsed.cleanText);
      }
    } catch {
      toast.error('Ошибка соединения с сервисом оценки. Повторите попытку.');
    } finally {
      setIsLoading(false);
    }
  };

  const parseAIResponse = (text: string): { badge?: 'CORRECT' | 'PARTIAL' | 'WRONG'; cleanText: string } => {
    let badge: 'CORRECT' | 'PARTIAL' | 'WRONG' | undefined = undefined;
    let cleanText = text;

    if (text.includes('[CORRECT]')) {
      badge = 'CORRECT';
      cleanText = text.replace('[CORRECT]', '').trim();
    } else if (text.includes('[PARTIAL]')) {
      badge = 'PARTIAL';
      cleanText = text.replace('[PARTIAL]', '').trim();
    } else if (text.includes('[WRONG]')) {
      badge = 'WRONG';
      cleanText = text.replace('[WRONG]', '').trim();
    }

    return { badge, cleanText };
  };

  const callOpenRouterAPI = async (contextMsgs: ChatMessage[]): Promise<string> => {
    for (let i = 0; i < AI_MODELS.length; i++) {
      const model = AI_MODELS[i];
      try {
        const response = await fetch(OPENROUTER_URL, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${OPENROUTER_KEY}`,
            'HTTP-Referer': 'https://study-platform.uz',
            'X-Title': 'Qualification Assessment',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: model,
            messages: contextMsgs.map(m => ({ role: m.role, content: m.content })),
            temperature: 0.6,
            max_tokens: 800
          })
        });

        if (response.status === 429 || response.status === 503) {
          continue;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const reply = data?.choices?.[0]?.message?.content;
        if (reply) return reply;
      } catch {
        // try next model
      }
    }
    throw new Error('Сервис временно недоступен.');
  };

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputVal).trim();
    if (!text || isLoading) return;

    setInputVal('');
    const userMsg: ChatMessage = {
      id: String(Date.now()),
      role: 'user',
      content: text,
      timestamp: Date.now()
    };

    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setIsLoading(true);

    const contextWithSystem: ChatMessage[] = [
      { id: 'sys', role: 'system', content: getSystemPrompt(selectedTopic), timestamp: 0 },
      ...newMsgs
    ];

    try {
      const reply = await callOpenRouterAPI(contextWithSystem);
      const parsed = parseAIResponse(reply);
      const aiMsg: ChatMessage = {
        id: String(Date.now() + 1),
        role: 'assistant',
        content: reply,
        evalBadge: parsed.badge,
        cleanContent: parsed.cleanText,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);

      if (selectedMode === 'voice') {
        setTranscript(parsed.cleanText);
        speakText(parsed.cleanText);
      }
    } catch {
      toast.error('Не удалось обработать ответ');
    } finally {
      setIsLoading(false);
    }
  };

  const speakText = (text: string) => {
    if (!synthRef.current || !soundEnabled) {
      setVoiceStatus('Ожидание ответа пользователя');
      return;
    }

    try {
      synthRef.current.cancel();
      const cleanForVoice = text
        .replace(/\[CORRECT\]/gi, 'Верно.')
        .replace(/\[PARTIAL\]/gi, 'Частично верно.')
        .replace(/\[WRONG\]/gi, 'Неверно.')
        .replace(/```[\s\S]*?```/g, 'код опущен')
        .replace(/[*_#`~]/g, '')
        .trim();

      const utterance = new SpeechSynthesisUtterance(cleanForVoice);
      utterance.lang = 'ru-RU';
      utterance.rate = 0.95;
      utterance.pitch = 0.95;

      utterance.onstart = () => {
        setIsSpeaking(true);
        setVoiceStatus('Воспроизведение вопроса...');
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setVoiceStatus('Нажмите кнопку микрофона для ответа');
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
        setVoiceStatus('Нажмите кнопку микрофона для ответа');
      };

      synthRef.current.speak(utterance);
    } catch {
      setIsSpeaking(false);
    }
  };

  const toggleMic = () => {
    if (isListening) {
      if (recognitionRef.current) recognitionRef.current.stop();
      setIsListening(false);
      setVoiceStatus('Ожидание');
      return;
    }

    const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRec) {
      toast.error('Браузер не поддерживает запись речи. Используйте текстовый режим.');
      return;
    }

    stopAllAudio();
    const recognition = new SpeechRec();
    recognition.lang = 'ru-RU';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceStatus('Идет запись ответа...');
    };

    recognition.onresult = (e: any) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      if (interim) {
        setTranscript(interim);
      }
      if (final) {
        setTranscript(final);
        handleSendMessage(final);
      }
    };

    recognition.onerror = () => {
      setIsListening(false);
      setVoiceStatus('Ошибка распознавания');
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  return (
    <div className="min-h-screen w-full bg-[#07030e] text-white flex flex-col font-sans relative">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0d051c]/80 backdrop-blur-2xl px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            type="button"
            onClick={() => { stopAllAudio(); isStarted ? setIsStarted(false) : navigate('/'); }}
            className="glass-btn-secondary px-3.5 py-2 rounded-xl text-xs uppercase tracking-wider flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{isStarted ? 'Завершить сессию' : 'В меню'}</span>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white text-[#07030e] font-black text-xs flex items-center justify-center shadow-lg shadow-white/10">
              SP
            </div>
            <div>
              <div className="font-bold text-xs uppercase tracking-wider text-white">
                Симулятор квалификационного собеседования
              </div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">
                Study Platform · Оценка соискателей
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isStarted && (
            <div className="hidden sm:inline-block px-3 py-1.5 rounded-xl bg-white/10 border border-white/15 text-xs font-semibold text-white">
              {selectedTopic.name}
            </div>
          )}

          <button
            type="button"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="glass-btn-secondary p-2.5 rounded-xl text-white"
            title={soundEnabled ? 'Отключить звук' : 'Включить звук'}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4 text-white" /> : <VolumeX className="w-4 h-4 text-white/40" />}
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col max-w-5xl w-full mx-auto p-4 sm:p-8">
        {!isStarted ? (
          <div className="flex-1 flex flex-col justify-center max-w-3xl mx-auto w-full py-4 space-y-8">
            <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-3">
              <div className="inline-block text-[11px] font-bold text-white/60 uppercase tracking-widest">
                Модуль профессиональной аттестации
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                Оценка квалификации и техническое собеседование
              </h1>
              <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                Система проводит стандартизированное интервью по профильным компетенциям, анализирует полноту ответов и формирует протокол оценки для работодателей.
              </p>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-white/70" />
                Направление аттестации:
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TOPICS.map((t) => {
                  const isSelected = selectedTopic.id === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTopic(t)}
                      className={`p-4 rounded-2xl cursor-pointer transition-all ${
                        isSelected 
                          ? 'glass-panel border-white/60 shadow-xl shadow-purple-950/40 scale-[1.01]' 
                          : 'glass-panel-subtle hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-bold text-xs uppercase text-white">{t.name}</span>
                        <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/80 border border-white/15">
                          {t.badge}
                        </span>
                      </div>
                      <div className="text-xs text-white/60 leading-relaxed">{t.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-white/70" />
                Режим взаимодействия:
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setSelectedMode('text')}
                  className={`p-5 rounded-2xl text-left transition-all ${
                    selectedMode === 'text'
                      ? 'glass-panel border-white/60'
                      : 'glass-panel-subtle hover:border-white/30'
                  }`}
                >
                  <div className="font-bold text-xs uppercase text-white">Текстовый опрос</div>
                  <div className="text-xs text-white/60 mt-1.5 leading-relaxed">Письменные ответы на вопросы с протоколированием аргументации.</div>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedMode('voice')}
                  className={`p-5 rounded-2xl text-left transition-all ${
                    selectedMode === 'voice'
                      ? 'glass-panel border-white/60'
                      : 'glass-panel-subtle hover:border-white/30'
                  }`}
                >
                  <div className="font-bold text-xs uppercase text-white">Голосовой опрос</div>
                  <div className="text-xs text-white/60 mt-1.5 leading-relaxed">Озвучивание вопросов экспертом и запись устных ответов кандидата.</div>
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={startInterview}
              className="glass-btn-primary w-full py-4 rounded-2xl text-xs uppercase tracking-wider flex items-center justify-center gap-2"
            >
              <FileCheck className="w-4 h-4" />
              <span>Начать аттестационное собеседование</span>
            </button>
          </div>
        ) : selectedMode === 'voice' ? (
          <div className="flex-1 flex flex-col items-center justify-between py-8 max-w-xl mx-auto w-full">
            <div className="w-full text-center space-y-2 glass-panel p-6 rounded-3xl">
              <div className="text-xs font-bold uppercase tracking-widest text-white/50">
                Голосовая сессия интервью
              </div>
              <div className="text-base font-bold text-white">
                {selectedTopic.name}
              </div>
              <div className="text-xs text-white/70">
                Статус: {voiceStatus}
              </div>
            </div>

            <div className="w-full my-8 p-6 rounded-3xl glass-panel-subtle min-h-[160px] flex items-center justify-center text-center text-sm leading-relaxed text-white">
              {transcript || (isLoading ? 'Генерация вопроса...' : 'Нажмите кнопку микрофона и дайте устный ответ.')}
            </div>

            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={toggleMic}
                disabled={isLoading}
                className={`w-18 h-18 p-5 rounded-full flex items-center justify-center transition-all ${
                  isListening
                    ? 'bg-rose-600 text-white shadow-xl shadow-rose-600/40 animate-pulse'
                    : 'glass-btn-primary shadow-xl shadow-white/20'
                }`}
                title={isListening ? 'Остановить запись' : 'Начать ответ'}
              >
                {isListening ? <MicOff className="w-7 h-7" /> : <Mic className="w-7 h-7" />}
              </button>

              <button
                type="button"
                onClick={() => {
                  stopAllAudio();
                  setSelectedMode('text');
                }}
                className="glass-btn-secondary px-4 py-3 rounded-xl text-xs uppercase tracking-wider"
              >
                В текст
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col h-full glass-panel rounded-3xl overflow-hidden">
            <div ref={chatBodyRef} className="flex-1 p-6 overflow-y-auto space-y-5">
              {messages.map((m) => {
                const isAI = m.role === 'assistant';
                return (
                  <div
                    key={m.id}
                    className={`flex flex-col ${isAI ? 'items-start' : 'items-end'}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1.5 px-1">
                      {isAI ? 'Экзаменатор' : 'Кандидат'}
                    </div>
                    <div className={`max-w-[85%] rounded-2xl p-4 text-xs sm:text-sm leading-relaxed ${
                      isAI 
                        ? 'glass-panel-subtle text-white border-white/10' 
                        : 'bg-white text-[#07030e] font-medium shadow-lg shadow-white/10'
                    }`}>
                      {m.evalBadge && (
                        <div className="mb-2 pb-2 border-b border-white/10 flex items-center gap-2 font-bold uppercase text-[10px]">
                          {m.evalBadge === 'CORRECT' && (
                            <span className="text-emerald-400">
                              Оценка: Верно
                            </span>
                          )}
                          {m.evalBadge === 'PARTIAL' && (
                            <span className="text-amber-400">
                              Оценка: Частично верно
                            </span>
                          )}
                          {m.evalBadge === 'WRONG' && (
                            <span className="text-rose-400">
                              Оценка: Неверно
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="whitespace-pre-wrap">
                        {m.cleanContent || m.content}
                      </div>
                    </div>
                  </div>
                );
              })}

              {isLoading && (
                <div className="text-xs text-white/50 px-2">
                  Обработка ответа и формирование вопроса...
                </div>
              )}
            </div>

            <div className="p-4 border-t border-white/10 bg-white/[0.02] flex items-center gap-3">
              <textarea
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Введите аргументированный ответ..."
                rows={1}
                className="flex-1 bg-white/[0.05] border border-white/15 rounded-xl px-4 py-3 text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/50 resize-none"
              />

              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputVal.trim()}
                className="glass-btn-primary px-5 py-3 rounded-xl text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
                <span>Отправить</span>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
